import React, { useRef, useState } from 'react';

const fmt = (s: number) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
};

// Lecteur audio compact et stylé (aux tokens du design refonte) : bouton play/pause rose,
// barre de progression seekable, temps courant / durée. Remplace le <audio controls> natif.
const RATES = [1, 1.5, 2] as const;

const AudioPlayer: React.FC<{ src: string; className?: string }> = ({ src, className = '' }) => {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [rate, setRate] = useState(1);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  // Cycle 1× → 1.5× → 2× → 1×, appliqué à l'élément audio.
  const cycleRate = () => {
    const next = RATES[(RATES.indexOf(rate as typeof RATES[number]) + 1) % RATES.length];
    setRate(next);
    if (ref.current) ref.current.playbackRate = next;
  };

  return (
    <div className={`flex items-center gap-3 bg-panel-2 border border-line rounded-lg px-3 py-2.5 ${className}`}>
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        className="hidden"
        onTimeUpdate={e => setCur(e.currentTarget.currentTime)}
        onLoadedMetadata={e => setDur(e.currentTarget.duration)}
        onEnded={() => setPlaying(false)}
      />
      <button onClick={toggle} aria-label={playing ? 'Pause' : 'Lecture'}
        className="w-9 h-9 rounded-full bg-hook-rose text-white flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity text-[13px] leading-none">
        {playing ? '❚❚' : '▶'}
      </button>
      <input type="range" min={0} max={dur || 0} step={0.1} value={cur}
        onChange={e => { const a = ref.current; if (a) { a.currentTime = Number(e.target.value); setCur(Number(e.target.value)); } }}
        className="flex-1 h-1.5 accent-hook-rose cursor-pointer" />
      <span className="font-mono text-[10px] text-dim tabular-nums whitespace-nowrap">{fmt(cur)} / {fmt(dur)}</span>
      <button onClick={cycleRate} aria-label="Vitesse de lecture"
        className="font-mono text-[10px] font-semibold px-1.5 py-1 rounded border border-line-2 text-dim hover:border-ink shrink-0 tabular-nums">
        {rate}×
      </button>
    </div>
  );
};

export default AudioPlayer;
